/**
 * useBeforeUnload - Hook to warn users when leaving with active uploads.
 *
 * Shows a browser confirmation dialog when the user tries to:
 * - Close the tab
 * - Navigate away
 * - Refresh the page
 *
 * Only active when there are uploads in progress.
 */

import { useEffect } from 'react';
import { useUploadProgressStore } from '../stores/uploadProgressStore';

/**
 * Hook to warn users when leaving with active uploads.
 *
 * Call this hook once at the app level (e.g., in App.tsx or AppProviders).
 * It automatically manages the beforeunload event based on upload state.
 */
export function useUploadWarning() {
  const hasActiveUploads = useUploadProgressStore((state) =>
    Object.values(state.states).some(s => s === 'queued' || s === 'uploading')
  );

  useEffect(() => {
    if (!hasActiveUploads) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Cancel the event
      e.preventDefault();
      // Modern browsers show a generic message, but we set this for older browsers
      e.returnValue = 'Files are still uploading. If you leave, uploads will be lost.';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasActiveUploads]);
}

/**
 * Hook to get the current upload warning state.
 *
 * Useful for displaying UI indicators about active uploads.
 */
export function useHasActiveUploads() {
  return useUploadProgressStore((state) =>
    Object.values(state.states).some(s => s === 'queued' || s === 'uploading')
  );
}
